import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Tabs,
  Tab,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// Stałe dla statusów klientów
const CLIENT_STATUSES = {
  VIP: 'VIP',
  Regular: 'Regulalny',
  New: 'Nowy',
  Inactive: 'Nieaktywny',
  Churned: 'Utracony'
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
      style={{ padding: '16px 0' }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientDetailsModal = ({ open, handleClose, clientId, onClientUpdate }) => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [client, setClient] = useState(null);
  const [editedClient, setEditedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Pobieranie danych klienta
  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId || !open) return;
      
      setLoading(true);
      try {
        // Pobierz dane klienta
        const clientDoc = await getDoc(doc(db, 'clients', clientId));
        if (clientDoc.exists()) {
          const clientData = {
            id: clientDoc.id,
            ...clientDoc.data()
          };
          setClient(clientData);
          setEditedClient(clientData);
        }
        
        // Pobierz kontakty klienta
        const contactsQuery = query(
          collection(db, 'contacts'),
          where('clientId', '==', clientId),
          orderBy('date', 'desc')
        );
        const contactsSnapshot = await getDocs(contactsQuery);
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContacts(contactsList);
        
        // Pobierz zakupy klienta
        const purchasesQuery = query(
          collection(db, 'sales'),
          where('clientId', '==', clientId),
          orderBy('date', 'desc')
        );
        const purchasesSnapshot = await getDocs(purchasesQuery);
        const purchasesList = purchasesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPurchases(purchasesList);
        
        // Pobierz pracowników (dla admina)
        if (isAdmin) {
          const usersQuery = query(collection(db, 'users'));
          const usersSnapshot = await getDocs(usersQuery);
          const employeesList = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setEmployees(employeesList);
        }
      } catch (error) {
        console.error('Błąd podczas pobierania danych klienta:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientData();
  }, [clientId, open, isAdmin]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedClient(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      // Przygotuj dane do aktualizacji
      const dataToUpdate = {
        ...editedClient,
        updated_at: new Date(),
        updated_by: {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || ''
        }
      };
      
      // Usuń pole id przed zapisem
      delete dataToUpdate.id;
      
      // Aktualizuj dokument w Firestore
      await updateDoc(doc(db, 'clients', clientId), dataToUpdate);
      
      // Zaktualizuj stan lokalny
      setClient({
        id: clientId,
        ...dataToUpdate
      });
      
      // Wyłącz tryb edycji
      setEditMode(false);
      
      // Wywołaj callback aktualizacji
      if (onClientUpdate) {
        onClientUpdate();
      }
    } catch (error) {
      console.error('Błąd podczas zapisywania zmian:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };
  
  const getEmployeeName = (employeeId) => {
    if (!employeeId) return 'Nieprzypisany';
    
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      return `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    }
    return 'Nieznany pracownik';
  };
  
  if (loading && !client) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!client) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography>Nie znaleziono klienta</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {`${client.firstName} ${client.lastName}`}
        </Typography>
        <Box>
          {editMode ? (
            <>
              <IconButton color="primary" onClick={handleSaveChanges} disabled={loading}>
                <SaveIcon />
              </IconButton>
              <IconButton color="error" onClick={() => setEditMode(false)} disabled={loading}>
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton color="primary" onClick={() => setEditMode(true)} disabled={loading}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="client tabs">
            <Tab label="Informacje" />
            <Tab label="Kontakty" />
            <Tab label="Zakupy" />
            <Tab label="Notatki" />
          </Tabs>
        </Box>
        
        {/* Zakładka z informacjami */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Imię"
                name="firstName"
                value={editMode ? editedClient.firstName : client.firstName}
                onChange={handleInputChange}
                disabled={!editMode}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwisko"
                name="lastName"
                value={editMode ? editedClient.lastName : client.lastName}
                onChange={handleInputChange}
                disabled={!editMode}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={editMode ? editedClient.email : client.email}
                onChange={handleInputChange}
                disabled={!editMode}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon"
                name="phone"
                value={editMode ? editedClient.phone : client.phone}
                onChange={handleInputChange}
                disabled={!editMode}
                margin="normal"
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                {editMode ? (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Przypisany do</InputLabel>
                    <Select
                      name="assignedTo"
                      value={editedClient.assignedTo || ''}
                      onChange={handleInputChange}
                      label="Przypisany do"
                    >
                      <MenuItem value="">Nieprzypisany</MenuItem>
                      {employees.map(employee => (
                        <MenuItem key={employee.id} value={employee.id}>
                          {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Przypisany do"
                    value={getEmployeeName(client.assignedTo)}
                    disabled
                    margin="normal"
                  />
                )}
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              {editMode ? (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={editedClient.status || 'New'}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {Object.entries(CLIENT_STATUSES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Status"
                  value={CLIENT_STATUSES[client.status] || client.status}
                  disabled
                  margin="normal"
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ostatni kontakt"
                value={client.lastContactDate ? 
                  format(client.lastContactDate.toDate(), 'dd.MM.yyyy', { locale: pl }) : 
                  'Brak kontaktu'
                }
                disabled
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Łącznie wydane"
                value={formatPrice(client.totalSpent || 0)}
                disabled
                margin="normal"
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Zakładka z kontaktami */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => {
                // Tu będzie otwieranie modalu do dodawania kontaktu
              }}
            >
              Dodaj kontakt
            </Button>
          </Box>
          
          {contacts.length === 0 ? (
            <Typography color="textSecondary" align="center">
              Brak historii kontaktów
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Opis</TableCell>
                    <TableCell>Pracownik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        {format(contact.date.toDate(), 'dd.MM.yyyy HH:mm', { locale: pl })}
                      </TableCell>
                      <TableCell>{contact.type}</TableCell>
                      <TableCell>{contact.description}</TableCell>
                      <TableCell>{getEmployeeName(contact.employeeId)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
        
        {/* Zakładka z zakupami */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => {
                // Tu będzie otwieranie modalu do dodawania zakupu
              }}
            >
              Dodaj zakup
            </Button>
          </Box>
          
          {purchases.length === 0 ? (
            <Typography color="textSecondary" align="center">
              Brak historii zakupów
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Produkt</TableCell>
                    <TableCell>Kwota</TableCell>
                    <TableCell>Pracownik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        {format(purchase.date.toDate(), 'dd.MM.yyyy', { locale: pl })}
                      </TableCell>
                      <TableCell>{purchase.productName}</TableCell>
                      <TableCell>{formatPrice(purchase.amount)}</TableCell>
                      <TableCell>{getEmployeeName(purchase.employeeId)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
        
        {/* Zakładka z notatkami */}
        <TabPanel value={tabValue} index={3}>
          <TextField
            fullWidth
            label="Notatki"
            name="notes"
            value={editMode ? editedClient.notes || '' : client.notes || ''}
            onChange={handleInputChange}
            disabled={!editMode}
            multiline
            rows={8}
            margin="normal"
            placeholder="Dodaj notatki o kliencie..."
          />
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientDetailsModal;
